// @flow
// Champs To Play list component with drag-and-drop reordering and filtering

import React, { useMemo, useState, useContext } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { Typography, Box } from '@mui/material';
import {
  StyledTableRow,
  StyledTableCell,
} from '../main-view-challenges/mastery-list/StylizedTableComponentsUtil';
import { getChampionPortraitURL } from '../top-level/ChampionUtils';
import ChampionDataContext from '../top-level/ChampionDataContext.react';
import { useChampsToPlayStorage } from './useChampsToPlayStorage';

type Props = {
  masteryData: Array<{
    champion_id: number,
    name: string,
    points: string,
  }>,
};

function ChampsToPlayList(props: Props): React.Element {
  const { version } = useContext(ChampionDataContext);
  const { champsToPlay, isLoaded, toggleToPlay, setComment, reorderList } = useChampsToPlayStorage();
  const [draggedName, setDraggedName] = useState(null);
  const [dragOverName, setDragOverName] = useState(null);
  const [filterText, setFilterText] = useState('');

  const sortedChampions = useMemo(() => {
    if (!isLoaded || !props.masteryData) return { toPlayChamps: [], otherChamps: [] };

    const toPlayChamps = [];
    const otherChamps = [];
    const seenNames = new Set();

    props.masteryData.forEach((champ) => {
      if (seenNames.has(champ.name)) return;
      seenNames.add(champ.name);

      const champData = champsToPlay[champ.name] || { toPlay: false, comment: '', order: 0 };
      const champWithData = { ...champ, ...champData };

      if (champData.toPlay) {
        toPlayChamps.push(champWithData);
      } else {
        otherChamps.push(champWithData);
      }
    });

    // Sort "to play" by order, then by name
    toPlayChamps.sort((a, b) => {
      if (a.order !== b.order) {
        return a.order - b.order;
      }
      return a.name.localeCompare(b.name);
    });

    // Sort others by name
    otherChamps.sort((a, b) => a.name.localeCompare(b.name));

    return { toPlayChamps, otherChamps };
  }, [props.masteryData, champsToPlay, isLoaded]);

  // Calculate preview order when dragging
  const filteredChampions = useMemo(() => {
    if (!filterText) return sortedChampions;

    const lowerFilter = filterText.toLowerCase();
    return {
      toPlayChamps: sortedChampions.toPlayChamps.filter((c) =>
        c.name.toLowerCase().includes(lowerFilter)
      ),
      otherChamps: sortedChampions.otherChamps.filter((c) =>
        c.name.toLowerCase().includes(lowerFilter)
      ),
    };
  }, [filterText, sortedChampions]);

  const previewToPlayChamps = useMemo(() => {
    if (!draggedName || !dragOverName) return filteredChampions.toPlayChamps;

    const list = filteredChampions.toPlayChamps;
    const draggedIndex = list.findIndex((c) => c.name === draggedName);
    const targetIndex = list.findIndex((c) => c.name === dragOverName);

    if (draggedIndex < 0 || targetIndex < 0) return list;

    // Show preview of reordered list
    const preview = [...list];
    const [removed] = preview.splice(draggedIndex, 1);
    preview.splice(targetIndex, 0, removed);

    return preview;
  }, [draggedName, dragOverName, filteredChampions.toPlayChamps]);

  const handleDragStart = (e, champName) => {
    setDraggedName(champName);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragEnd = () => {
    setDraggedName(null);
    setDragOverName(null);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDragEnter = (e, champName) => {
    setDragOverName(champName);
  };

  const handleDragLeave = () => {
    setDragOverName(null);
  };

  const handleDrop = (e, targetName) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverName(null);

    if (!draggedName || draggedName === targetName) {
      setDraggedName(null);
      return;
    }

    // Find the list the dragged item belongs to
    const list = sortedChampions.toPlayChamps.some((c) => c.name === draggedName)
      ? sortedChampions.toPlayChamps
      : sortedChampions.otherChamps;

    const draggedIndex = list.findIndex((c) => c.name === draggedName);
    const targetIndex = list.findIndex((c) => c.name === targetName);

    if (draggedIndex < 0 || targetIndex < 0) {
      setDraggedName(null);
      return;
    }

    // Perform the reorder
    const reordered = [...list];
    const [removed] = reordered.splice(draggedIndex, 1);
    reordered.splice(targetIndex, 0, removed);

    // Update storage with single call
    reorderList(reordered);

    setDraggedName(null);
  };

  if (!isLoaded) {
    return <Typography style={{ color: 'white' }}>Loading...</Typography>;
  }

  const getRowStyle = (champName, isInToPlaySection) => {
    const style = { color: 'white' };

    if (isInToPlaySection) {
      style.backgroundColor = 'rgba(249, 115, 115, 0.2)';
    }

    if (draggedName === champName) {
      style.opacity = '0.4';
      style.cursor = 'grabbing';
      style.backgroundColor = 'rgba(249, 115, 115, 0.5)';
      style.transition = 'all 0.15s ease-out';
    } else if (dragOverName === champName && draggedName) {
      style.boxShadow = 'inset 0 -4px 0 rgba(249, 115, 115, 1)';
      style.backgroundColor = 'rgba(249, 115, 115, 0.35)';
      style.transition = 'all 0.15s ease-out';
    } else {
      style.cursor = isInToPlaySection ? 'grab' : 'default';
    }

    return style;
  };

  const renderChampionRow = (champ, isInToPlaySection) => (
    <StyledTableRow
      key={`${isInToPlaySection ? 'toplay' : 'other'}-${champ.name}`}
      style={getRowStyle(champ.name, isInToPlaySection)}
      onDragStart={(e) => handleDragStart(e, champ.name)}
      onDragEnd={handleDragEnd}
      onDragOver={handleDragOver}
      onDragEnter={(e) => handleDragEnter(e, champ.name)}
      onDragLeave={handleDragLeave}
      onDrop={(e) => handleDrop(e, champ.name)}
      draggable={isInToPlaySection}
      title={isInToPlaySection ? 'Drag to reorder' : ''}
    >
      <StyledTableCell style={{ width: 60, padding: '12px 16px' }}>
        <div className="masteryListChampionPortrait">
          <img
            src={getChampionPortraitURL(champ.name, version)}
            className="masteryListChampionPortrait"
            alt={champ.name}
          />
        </div>
      </StyledTableCell>
      <StyledTableCell component="th" scope="row" style={{ color: 'white', padding: '12px 16px' }}>
        {champ.name}
      </StyledTableCell>
      <StyledTableCell style={{ color: 'white', padding: '12px 16px' }}>
        {Number(champ.points).toLocaleString()}
      </StyledTableCell>
      <StyledTableCell style={{ color: 'white', maxWidth: 250, padding: '12px 16px' }}>
        <input
          type="text"
          value={champsToPlay[champ.name]?.comment || ''}
          onChange={(e) => setComment(champ.name, e.target.value)}
          placeholder="Add note..."
          className="champComment"
          style={{
            backgroundColor: 'rgba(255, 255, 255, 0.08)',
            color: 'whitesmoke',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            borderRadius: '4px',
            padding: '6px 8px',
            width: '100%',
            fontFamily: 'inherit',
            fontSize: '13px',
          }}
        />
      </StyledTableCell>
      <StyledTableCell style={{ width: 40, padding: '12px 16px' }}>
        <button
          onClick={() => toggleToPlay(champ.name)}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            fontSize: '20px',
            color: isInToPlaySection ? '#f97373' : 'rgba(249, 115, 115, 0.5)',
            padding: 0,
          }}
          title={isInToPlaySection ? 'Remove from To Play' : 'Add to To Play'}
        >
          {isInToPlaySection ? '★' : '☆'}
        </button>
      </StyledTableCell>
    </StyledTableRow>
  );

  return (
    <div className="champsToPlayList">
      {/* Filter Input */}
      <Box sx={{ mb: 3, ml: 2, mr: 2 }}>
        <input
          type="text"
          placeholder="Filter champions..."
          value={filterText}
          onChange={(e) => setFilterText(e.target.value)}
          style={{
            width: '100%',
            maxWidth: '400px',
            padding: '10px 12px',
            fontSize: '14px',
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            color: 'white',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            borderRadius: '4px',
            fontFamily: 'inherit',
          }}
        />
      </Box>

      {/* To Play Section */}
      {filteredChampions.toPlayChamps.length > 0 && (
        <Box className="champSection" sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ color: 'white', mb: 2, ml: 2, fontWeight: 'bold' }}>
            Champions To Play ({filteredChampions.toPlayChamps.length})
          </Typography>
          <TableContainer>
            <Table sx={{ color: 'white', minWidth: 700, border: '1px solid white' }} aria-label="champs to play table">
              <TableHead>
                <TableRow>
                  <StyledTableCell style={{ width: 60, padding: '16px' }} />
                  <StyledTableCell style={{ width: 100, padding: '16px' }}>Champion</StyledTableCell>
                  <StyledTableCell style={{ padding: '16px' }}>Mastery</StyledTableCell>
                  <StyledTableCell style={{ padding: '16px' }}>Notes</StyledTableCell>
                  <StyledTableCell style={{ width: 40, padding: '16px' }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {previewToPlayChamps.map((champ) => renderChampionRow(champ, true))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* All Champions Section */}
      <Box className="champSection">
        <Typography variant="h6" sx={{ color: 'white', mb: 2, ml: 2, fontWeight: 'bold' }}>
          All Champions ({filteredChampions.otherChamps.length})
        </Typography>
        <TableContainer>
          <Table sx={{ color: 'white', minWidth: 700, border: '1px solid white' }} aria-label="all champions table">
            <TableHead>
              <TableRow>
                <StyledTableCell style={{ width: 60, padding: '16px' }} />
                <StyledTableCell style={{ width: 100, padding: '16px' }}>Champion</StyledTableCell>
                <StyledTableCell style={{ padding: '16px' }}>Mastery</StyledTableCell>
                <StyledTableCell style={{ padding: '16px' }}>Notes</StyledTableCell>
                <StyledTableCell style={{ width: 40, padding: '16px' }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredChampions.otherChamps.map((champ) => renderChampionRow(champ, false))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </div>
  );
}

export default ChampsToPlayList;
